import { Button, CircularProgress } from '@mui/material';
import { VerifyDialog } from 'components/common';
import { Organization } from 'types';
import { DIALOG_ICON_TYPES } from 'components/common/VerifyDialog/constants';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import { useMemo, useState } from 'react';
import { useOrganizationQuery } from 'store/queries/organizations/useOrganizationQuery';
import { useChangeTenantAdminMutation } from 'store/queries/organizations/useChangeTenantAdminMutation';
import { Combobox } from '@globalvisioninc/ui/combobox';
import styles from './ChangeTenantAdminModal.module.css';

interface ChangeTenantAdminModalProps {
  open: boolean;
  onClose: () => void;
  orgInfo?: Organization;
}

const ChangeTenantAdminModal = ({ open, onClose, orgInfo }: ChangeTenantAdminModalProps) => {
  if (!orgInfo) {
    return null;
  }

  const [newAdminEmail, setNewAdminEmail] = useState('');
  // Portal target inside the dialog so Combobox options stay outside MUI aria-hidden siblings.
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);
  const { data, isFetching } = useOrganizationQuery(orgInfo.id, { includeUsers: true });
  const { mutateAsync, isPending } = useChangeTenantAdminMutation();

  const orgActiveUserOptions = useMemo(() => {
    const options: Array<{ label: string; value: string }> = [];
    for (const member of data?.members ?? []) {
      if (member.status !== 'active' || !member.email || member.email === orgInfo.adminEmail) {
        continue;
      }
      options.push({ label: member.email, value: member.email });
    }
    return options;
  }, [data?.members, orgInfo.adminEmail]);

  const handleEmailChange = (value: string) => {
    setNewAdminEmail(value);
  };

  const handleSubmit = async () => {
    if (!newAdminEmail) return;

    await mutateAsync({ newAdminEmail, orgId: orgInfo.id, orgName: orgInfo.name });
    onClose();
  };

  return (
    <VerifyDialog
      title={'Change Tenant Admin'}
      subTitle={'Change the Tenant Administrator for the selected tenant'}
      callToActionText=""
      iconType={DIALOG_ICON_TYPES.SWAP}
      open={open}
      isDivider
      handleCloseModal={onClose}
      hideCallToAction
      handleCallToActionClick={() => null}
      additionalActions={
        <>
          <Button color="inherit" id="change-tenant-admin-form-cancel-button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            id="dialog-ok"
            disabled={!newAdminEmail || isPending}
            color="secondary"
            variant="contained"
            onClick={handleSubmit}
          >
            {'save'}
            {isPending && <CircularProgress className={styles.spinner} size={16} color="primary" />}
          </Button>
        </>
      }
    >
      <div ref={setPortalContainer} className={styles.container}>
        <GVTextField id="admin-email-tenant" label="Current Tenant Admin Email" value={orgInfo.adminEmail} isDisabled />
        <Combobox.Root
          options={orgActiveUserOptions}
          value={newAdminEmail}
          onValueChange={handleEmailChange}
          isLoading={isFetching}
          isDisabled={isPending}
          hasClearButton
          container={portalContainer}
        >
          <Combobox.Label>New Tenant Admin Email</Combobox.Label>
          <Combobox.Input />
        </Combobox.Root>
      </div>
    </VerifyDialog>
  );
};

export default ChangeTenantAdminModal;
